import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ForceGraph2D from "react-force-graph-2d";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PARTY_COLOR = { D: "#3b82f6", R: "#ef4444", I: "#a855f7" };

const MiniGraph = ({ congress, chamber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const boxRef = React.useRef(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const measure = () => boxRef.current && setW(boxRef.current.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);
  useEffect(() => {
    setLoading(true);
    const params = { signal: "votes", congress };
    if (chamber) params.chamber = chamber;
    axios.get(`${API}/network/graph`, { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [congress, chamber]);

  const drawNode = useCallback((node, ctx) => {
    ctx.beginPath();
    ctx.arc(node.x, node.y, 2.6, 0, 2 * Math.PI);
    ctx.fillStyle = PARTY_COLOR[node.party] || "#9ca3af";
    ctx.fill();
  }, []);

  return (
    <div ref={boxRef} className="relative whip-surface rounded-xl border border-white/10 overflow-hidden" data-testid={`compare-graph-${congress}`}>
      <div className="absolute top-3 left-3 z-10 bg-[#10141d]/90 border border-white/10 rounded-lg px-3 py-1.5">
        <p className="text-sm font-bold text-white">{congress}th Congress</p>
        {data?.meta && (
          <p className="text-[10px] font-mono text-white/50">
            {data.meta.members} members · {data.meta.edges} ties · <span className="text-yellow-300/90">{data.meta.crossPartyEdges} cross-party</span>
          </p>
        )}
      </div>
      {loading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0b0e14]/70"><Loader2 className="w-5 h-5 text-orange-400 animate-spin" /></div>}
      <ForceGraph2D
        width={w} height={520}
        graphData={data ? { nodes: data.nodes.map((n) => ({ ...n })), links: data.links.map((l) => ({ ...l })) } : { nodes: [], links: [] }}
        backgroundColor="#0b0e14"
        nodeCanvasObject={drawNode}
        nodeLabel={(n) => `<div style="background:#141924;border:1px solid #2a3348;border-radius:8px;padding:6px 9px;font-size:12px;color:#e6e9f0;"><b>${n.name}</b> (${n.party}-${n.state})<br/><span style="color:#f97316">${n.communityLabel || ""}</span></div>`}
        linkColor={(l) => (l.crossParty ? "rgba(234,179,8,0.5)" : "rgba(255,255,255,0.10)")}
        linkWidth={(l) => (l.crossParty ? 1.2 : 0.5)}
        cooldownTicks={100}
        enableNodeDrag={false}
      />
      {data?.communities?.length > 0 && (
        <div className="absolute bottom-3 left-3 bg-[#10141d]/90 border border-white/10 rounded-lg px-3 py-2 max-w-[280px]">
          <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1">Largest groups</p>
          {data.communities.slice(0, 3).map((c) => (
            <p key={c.id} className="text-[11px] text-white/70 truncate">· {c.label} <span className="text-white/35">({c.size})</span></p>
          ))}
        </div>
      )}
    </div>
  );
};

export const CompareView = ({ chamber }) => {
  return (
    <div data-testid="compare-view">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <MiniGraph congress={119} chamber={chamber} />
        <MiniGraph congress={118} chamber={chamber} />
      </div>
      <p className="text-[11px] text-white/40 mt-3 max-w-3xl" data-testid="compare-note">
        Same voting-alignment network, two Congresses side by side. Watch how the cross-party tie count and the shape of the two party clouds change, a tighter, more separated pair of clusters means rising polarization; more yellow lines means more aisle-crossing. The 118th view reflects the roll calls in WHIP's archive for that Congress, so it is a sample rather than the full record.
      </p>
    </div>
  );
};

export default CompareView;
